import {
  type ChangeEvent,
  type FocusEvent,
  type KeyboardEvent as ReactKeyboardEvent
} from "react"
import type { UseFieldEditorReturn } from "@/hooks/useFieldEditor"
import type { ExtractedField, FieldOption } from "../types"
import { toISODateString, formatDisplayDate } from "@/lib/dateUtils"
import { DateFieldEditor } from "@/components/ui/date-picker"

export interface EditableValueOptions {
  placeholder?: string
  buttonClassName?: string
  inputClassName?: string
  containerClassName?: string
  placeholderClassName?: string
  selectOptions?: ReadonlyArray<{ label: string; value: string }>
  selectPlaceholder?: string
  readOnly?: boolean
}

export interface LineItemErpDraft {
  deal_name?: number
  acc_assignment?: number
  analytic_parts?: Array<{
    acc: string
    level1?: string
    level1_name?: string
    level2?: string
    level2_name?: string
    level3?: string
    level3_name?: string
  }>
}

export interface LineItemDraft {
  description: string
  quantity: string
  unitPrice: string
  unit: string
  total: string
  vatRate: string
  discountPercent: string
  discountAmount: string
  erp: LineItemErpDraft
}

export interface SerializedLineItem {
  source_group_id?: string
  description: string
  quantity: string
  unit_price: string
  unit?: string
  line_total: string
  vat_rate?: string
  discount_percent?: string
  discount_amount?: string
  merged_from?: string[]
}

export interface LineItemRow {
  id: string
  fields: ExtractedField[]
  description?: ExtractedField
  unitOfMeasure?: ExtractedField
  // Numeric/monetary fields from representation (not ExtractedField)
  quantity: string | null
  unitPrice: string | null
  lineTotal: string | null
  lineTax: string | null
  vatRate: string | null
  discountPercent: string | null
  discountAmount: string | null
}

export const createEmptyDraft = (): LineItemDraft => ({
  description: "",
  quantity: "",
  unitPrice: "",
  unit: "",
  total: "",
  vatRate: "20",
  discountPercent: "",
  discountAmount: "",
  erp: {}
})

const parseNumericValue = (raw?: string | null): number | null => {
  if (raw === undefined || raw === null) {
    return null
  }

  const normalized = raw.toString().trim().replace(/\s+/g, "")
  if (normalized === "") {
    return null
  }

  const candidate = Number(normalized.replace(",", "."))
  return Number.isFinite(candidate) ? candidate : null
}

const formatNumericValue = (value: number | null): string => {
  if (value === null) {
    return ""
  }

  if (Number.isInteger(value)) {
    return value.toString()
  }

  return value.toFixed(2)
}

export const computeDraftFromRows = (rows: LineItemRow[]): LineItemDraft => {
  if (rows.length === 0) {
    return createEmptyDraft()
  }

  const descriptions = rows.map((row) => row.description?.field_value ?? "").filter(Boolean)
  const units = rows.map((row) => row.unitOfMeasure?.field_value ?? "").filter(Boolean)
  const vatRates = rows.map((row) => row.vatRate ?? "").filter(Boolean)

  // Sum net amounts (qty * unit_price) per row to get the merged unit price.
  // When qty is null, treat as 1 (matches backend recalculate_header_totals).
  // Only fall back to lineTotal (gross) as a last resort.
  const netAmounts = rows.map((row) => {
    const qty = parseNumericValue(row.quantity)
    const price = parseNumericValue(row.unitPrice)
    if (qty !== null && price !== null) return qty * price
    if (price !== null) return price
    return parseNumericValue(row.lineTotal)
  })
  const netSum = netAmounts.reduce((acc: number, value) => (value !== null ? acc + value : acc), 0)
  const hasNet = netAmounts.some((value) => value !== null)

  const uniqueVatRates = [...new Set(vatRates)]
  const vatRateValue = uniqueVatRates.length === 1 ? uniqueVatRates[0] : (vatRates[0] ?? "20")

  return {
    description: descriptions[0] ?? "",
    quantity: "1",
    unitPrice: hasNet ? formatNumericValue(netSum) : "",
    unit: units[0] ?? "",
    total: hasNet ? formatNumericValue(netSum) : "",
    vatRate: vatRateValue,
    discountPercent: "",
    discountAmount: "",
    erp: {}
  }
}

interface LineItemErpMetadataEntry {
  deal_name?: number
  acc_assignment?: number
  analytic_parts?: Array<{ acc: string; level1?: string; level1_name?: string; level2?: string; level2_name?: string; level3?: string; level3_name?: string }>
}

/** Pick the value that appears most often from a map of value → count. */
function majorityValue<T>(counts: Map<T, number>): T | undefined {
  let best: T | undefined
  let bestCount = 0
  for (const [val, cnt] of counts) {
    if (cnt > bestCount) {
      best = val
      bestCount = cnt
    }
  }
  return best
}

/**
 * Compute ERP draft from selected line items' metadata using majority-vote.
 * - deal_name / acc_assignment: most common value across selected items
 * - analytic_parts: taken from the first item matching the majority acc_assignment
 */
export function computeErpDraftFromRows(
  selectedIds: string[],
  lineItemsErpMetadata: Record<string, LineItemErpMetadataEntry>
): LineItemErpDraft {
  const metas = selectedIds.map((id) => lineItemsErpMetadata[id]).filter(Boolean)
  if (metas.length === 0) return {}

  const dealCounts = new Map<number, number>()
  const accCounts = new Map<number, number>()
  for (const m of metas) {
    if (m.deal_name != null) dealCounts.set(m.deal_name, (dealCounts.get(m.deal_name) ?? 0) + 1)
    if (m.acc_assignment != null) accCounts.set(m.acc_assignment, (accCounts.get(m.acc_assignment) ?? 0) + 1)
  }

  const deal_name = majorityValue(dealCounts)
  const acc_assignment = majorityValue(accCounts)

  // Take analytic_parts from the first item that matches the majority acc_assignment
  const matching = metas.find((m) => m.acc_assignment === acc_assignment)
  const analytic_parts = matching?.analytic_parts ?? []

  return { deal_name, acc_assignment, analytic_parts }
}

export const trimValue = (value: string): string => value.trim()

export const parseInvoiceDateToISO = (value?: string | null): string | null => {
  if (!value) {
    return null
  }

  return toISODateString(value)
}

export const normalizeCurrencyCodeInput = (value: string): string =>
  value.replace(/[^A-Za-z]/g, "").slice(0, 3).toUpperCase()

export const resolveSelectValue = (
  value: string | null | undefined,
  options: ReadonlyArray<FieldOption>
): string | undefined => {
  if (!value) {
    return undefined
  }

  const normalized = String(value)
  const match =
    options.find((option) => option.value === normalized) ??
    options.find((option) => option.code === normalized) ??
    options.find((option) => option.id === normalized) ??
    options.find((option) => option.label === normalized)

  return match?.value
}

/**
 * Check whether a field represents a date based on its name.
 */
export const isDateField = (field: ExtractedField): boolean =>
  field.field_name.toLowerCase().includes("date")

/**
 * Return the edit-mode value for a field.
 * For date fields the raw ISO value is converted to DD.MM.YYYY so that the
 * user sees the display format while typing.
 */
export const getEditableValue = (field: ExtractedField): string => {
  const raw = field.field_value ?? ""
  if (!isDateField(field) || raw === "") return raw
  return formatDisplayDate(raw) || raw
}

export const formatFieldDisplayValue = (field: ExtractedField): string => {
  const rawValue = field.field_value
  if (rawValue === undefined || rawValue === null) {
    return ""
  }

  const rawString = String(rawValue).trim()
  if (rawString === "") {
    return ""
  }

  const isDateField = field.field_name.toLowerCase().includes("date")
  if (!isDateField) {
    return rawString
  }

  const formatted = formatDisplayDate(rawString)
  return formatted || rawString
}

export interface EditableValueRendererDependencies {
  fieldEditor: UseFieldEditorReturn
  editableFieldAttr: Record<string, string>
  focusTargetAttr: Record<string, string>
  handleFieldActivation: (field: ExtractedField) => void
  handleFieldBlur: (
    event: FocusEvent<HTMLInputElement | HTMLSelectElement>,
    fieldId: string
  ) => void
  handleFieldInputKeyDown: (
    event: ReactKeyboardEvent<HTMLInputElement | HTMLSelectElement>
  ) => void
  handleFieldSelectChange: (event: ChangeEvent<HTMLSelectElement>) => void
  handleFieldValueChange: (
    event: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void
  formatDisplayValue: (field: ExtractedField) => string
  onDatePickerSelect?: (fieldId: string, isoValue: string) => void
}

export type EditableValueRenderer = (
  targetField?: ExtractedField,
  options?: EditableValueOptions
) => JSX.Element

export const createEditableValueRenderer = ({
  fieldEditor,
  editableFieldAttr,
  focusTargetAttr,
  handleFieldActivation,
  handleFieldBlur,
  handleFieldInputKeyDown,
  handleFieldSelectChange,
  handleFieldValueChange,
  formatDisplayValue,
  onDatePickerSelect
}: EditableValueRendererDependencies): EditableValueRenderer => {
  return (targetField, options) => {
    const placeholderText = options?.placeholder ?? "No value"
    const placeholderElement = (
      <span className={options?.placeholderClassName ?? "text-gray-400 italic"}>
        {placeholderText}
      </span>
    )

    if (!targetField) {
      return placeholderElement
    }

    const isEditingField = fieldEditor.isEditing(targetField.id)

    if (isEditingField) {
      const containerClass = options?.containerClassName
        ? `flex min-w-0 items-center w-full ${options.containerClassName}`
        : "flex min-w-0 items-center space-x-2 w-full"

      if (options?.selectOptions && options.selectOptions.length > 0) {
        const selectPlaceholder = options.selectPlaceholder ?? "Select an option"

        return (
          <div className={containerClass}>
            <select
              value={fieldEditor.editValue || ""}
              onChange={handleFieldSelectChange}
              onBlur={(event) => handleFieldBlur(event, targetField.id)}
              className={`w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${options?.inputClassName ?? ""}`.trim()}
              {...focusTargetAttr}
              autoFocus
            >
              <option value="">{selectPlaceholder}</option>
              {options.selectOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )
      }

      // Date fields get a text input with auto-opening calendar popover
      if (isDateField(targetField)) {
        return (
          <div className={containerClass}>
            <DateFieldEditor
              value={fieldEditor.editValue}
              onChange={handleFieldValueChange}
              onBlur={(event) => handleFieldBlur(event, targetField.id)}
              onKeyDown={handleFieldInputKeyDown}
              onDateSelect={(day) => {
                const iso = toISODateString(day)
                if (iso && onDatePickerSelect) {
                  onDatePickerSelect(targetField.id, iso)
                }
                fieldEditor.cancelEditing()
              }}
              inputClassName={`w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${options?.inputClassName ?? ""}`.trim()}
              focusTargetAttr={focusTargetAttr}
              autoFocus
            />
          </div>
        )
      }

      return (
        <div className={containerClass}>
          <input
            type="text"
            value={fieldEditor.editValue}
            onChange={handleFieldValueChange}
            onFocus={(event) => event.target.select()}
            onBlur={(event) => handleFieldBlur(event, targetField.id)}
            onKeyDown={handleFieldInputKeyDown}
            className={`w-full text-sm border border-gray-300 rounded px-2 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${options?.inputClassName ?? ""}`.trim()}
            {...focusTargetAttr}
            autoFocus
          />
        </div>
      )
    }

    const hasValue =
      targetField.field_value !== undefined &&
      targetField.field_value !== null &&
      targetField.field_value !== ""

    const displayValue = formatDisplayValue(targetField)

    if (options?.readOnly) {
      return (
        <span
          className={`w-full min-w-0 max-w-full text-left text-sm font-semibold text-neutral-950 leading-5 break-words whitespace-normal rounded px-1 py-1 ${options?.buttonClassName ?? ""}`.trim()}
        >
          {hasValue ? displayValue : placeholderElement}
        </span>
      )
    }

    return (
      <button
        type="button"
        {...editableFieldAttr}
        {...focusTargetAttr}
        className={`w-full min-w-0 max-w-full text-left text-sm font-semibold text-neutral-950 leading-5 break-words whitespace-normal cursor-pointer hover:bg-gray-50 rounded px-1 py-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 ${options?.buttonClassName ?? ""}`.trim()}
        onFocus={() => handleFieldActivation(targetField)}
        onClick={(event) => {
          event.stopPropagation()
          handleFieldActivation(targetField)
        }}
      >
        {hasValue ? displayValue : placeholderElement}
      </button>
    )
  }
}
